import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { IDADE_LABELS, SITUACAO_DO_CLIENTE_LABELS, nomeDoMes, type SituacaoDoCliente } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { ExtratoDoClienteDialog } from "./ExtratoDoClienteDialog";

const COR_DA_SITUACAO: Record<SituacaoDoCliente, string> = {
  QUITADO: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  PARCIAL: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  EM_ABERTO: "bg-zinc-100 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
  CREDITO: "bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary",
  PERDIDO: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400",
};
const COR_DA_IDADE = { ATE_30: "text-amber-600", DE_31_A_60: "text-orange-600", MAIS_DE_60: "text-red-600" } as const;
const moldura = "overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card";

/** Extrato por cliente e mês: o faturado sai dos pedidos concluídos; aqui só se registra o que
 *  entrou. Com os extratos ligados, sobra só o que é de antes do início deles (`legado`). */
export function AReceberPorMes({ competencia, legado = false }: { competencia: string; legado?: boolean }) {
  const dados = trpc.financeiro.aReceber.resumo.useQuery({ competencia }).data;
  const [aberto, setAberto] = useState<{ clienteId: string; competencia: string } | null>(null);
  const doMes = dados?.doMes ?? [];
  const anteriores = dados?.anteriores ?? [];
  const soma = (k: "faturadoCents" | "recebidoCents") => doMes.reduce((t, c) => t + c[k], 0);
  const emAbertoDoMes = doMes.reduce((t, c) => t + Math.max(0, c.saldoCents), 0);
  const atrasado = anteriores.reduce((t, c) => t + c.saldoCents, 0);

  const cartoes = [
    { rotulo: "Faturado no mês", valor: soma("faturadoCents"), cor: "text-zinc-900 dark:text-foreground" },
    { rotulo: "Recebido", valor: soma("recebidoCents"), cor: "text-emerald-600" },
    { rotulo: "Em aberto do mês", valor: emAbertoDoMes, cor: "text-zinc-900 dark:text-foreground" },
    { rotulo: "Atrasado de meses anteriores", valor: atrasado, cor: atrasado ? "text-red-600" : "text-zinc-900 dark:text-foreground" },
  ];

  if (legado && dados && doMes.length === 0 && anteriores.length === 0) return null;

  return (
    <div className="space-y-5">
      {legado && <h3 className="pt-2 text-sm font-semibold text-zinc-900 dark:text-foreground">Antes dos extratos (por mês)</h3>}
      <div className={cn("grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4", legado && "hidden")}>
        {cartoes.map((c) => (
          <div key={c.rotulo} className="min-w-0 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm sm:p-4 dark:border-border dark:bg-card">
            <p className="truncate text-[11px] font-medium text-zinc-500 sm:text-xs dark:text-muted-foreground">{c.rotulo}</p>
            <p className={cn("mt-1 truncate text-base font-bold tabular-nums sm:text-xl", c.cor)}>{reais(c.valor)}</p>
          </div>
        ))}
      </div>

      <section className={cn(moldura, legado && doMes.length === 0 && "hidden")}>
        <header className="border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Clientes em {nomeDoMes(competencia)}</h3>
        </header>
        {dados && doMes.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum serviço concluído neste mês.</p>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-border/50">
            {doMes.map((c) => (
              <li key={c.cliente}>
                <button type="button" onClick={() => setAberto({ clienteId: c.cliente, competencia })} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-zinc-50/60 dark:hover:bg-muted/40">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{c.nome}</span>
                    <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                      {c.servicos} {c.servicos === 1 ? "serviço" : "serviços"} · faturado {reais(c.faturadoCents)} · recebido {reais(c.recebidoCents)}
                      {c.semPreco > 0 && <span className="font-semibold text-amber-600"> · {c.semPreco} sem preço</span>}
                    </span>
                  </span>
                  <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase", COR_DA_SITUACAO[c.situacao])}>{SITUACAO_DO_CLIENTE_LABELS[c.situacao]}</span>
                  <span className="w-28 text-right text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{reais(Math.max(0, c.saldoCents))}</span>
                  <ChevronRight className="h-4 w-4 text-zinc-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {anteriores.length > 0 && (
        <section className={moldura}>
          <header className="border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Em atraso de meses anteriores</h3>
          </header>
          <ul className="divide-y divide-zinc-100 dark:divide-border/50">
            {anteriores.map((c) => (
              <li key={`${c.cliente}-${c.competencia}`}>
                <button type="button" onClick={() => setAberto({ clienteId: c.cliente, competencia: c.competencia })} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-zinc-50/60 dark:hover:bg-muted/40">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{c.nome}</span>
                    <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">Extrato de {nomeDoMes(c.competencia)}</span>
                  </span>
                  <span className={cn("text-xs font-semibold", COR_DA_IDADE[c.idade])}>
                    {c.dias} dias · {IDADE_LABELS[c.idade]}
                  </span>
                  <span className="w-28 text-right text-sm font-semibold tabular-nums text-red-600">{reais(c.saldoCents)}</span>
                  <ChevronRight className="h-4 w-4 text-zinc-400" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ExtratoDoClienteDialog alvo={aberto} onFechar={() => setAberto(null)} />
    </div>
  );
}
